import { useEffect } from "react";
import { setGlobalCustomDimension, trackEvent } from "@leafly-com/web-utils";
import Head from "next/head";
import Link from "next/link";

import getUser from "api/requests/getUser";
import useDomainCountryCode from "hooks/useDomainCountryCode";
import { useDispatch, useSelector } from "redux/hooks";
import { USER_LOAD, UserLoadAction } from "redux/reducers/user";
import { getFullUser } from "redux/selectors/user";
import { localizeText } from "utils/localizeText";

import UserProfileMobileNavigation from "./UserProfileMobileNavigation";

export const NAV_ITEMS = (countryCode: string) => {
  return [
    {
      name: "Favorite strains",
      url: "/me/favorites",
    },
    {
      name: "Recommended strains",
      url: "/me/recommended-strains",
    },
    {
      name: "Reviews",
      url: "/me/reviews",
    },
    {
      name: `${localizeText("Dispensary", countryCode)} updates`,
      url: "/me/updates",
    },
    {
      name: "Edit profile",
      url: "/me/edit-profile",
    },
    {
      name: "Settings",
      url: "/me/settings",
    },
  ];
};

const UserProfileLayout = ({
  children,
  currentPage,
}: {
  children: React.ReactNode;
  currentPage: string;
}) => {
  const dispatch = useDispatch();
  const countryCode = useDomainCountryCode();
  const user = useSelector(getFullUser);

  useEffect(() => {
    if (!user.id || !user.username) {
      getUser(countryCode).then((userData) => {
        dispatch<UserLoadAction>({
          type: USER_LOAD,
          userData,
        });
      });
    }
  }, []);

  useEffect(() => {
    if (user?.id && user.id > 0) {
      setGlobalCustomDimension("userId", user.id);
    }
  }, [user.id]);

  return (
    <>
      <Head>
        <title>
          {user?.username
            ? `${user.username}'s Profile — Leafly`
            : `Loading profile`}
        </title>
        <meta name="robots" content="noindex, nofollow, noarchive" />
      </Head>
      <div className="container">
        <div className="mobile-dropdown md:hidden mt-xxl">
          <UserProfileMobileNavigation currentPage={currentPage} />
        </div>
        <div className="row mt-xl md:mt-none isolate">
          <div className="hidden md:block md:col-1/4 navigation">
            <ul>
              {NAV_ITEMS(countryCode).map((navItem, i) => (
                <li key={i} className="nav-item mb-md">
                  <Link
                    href={navItem.url}
                    className={`nav-item text-md relative ${
                      currentPage === navItem.name && "selected"
                    }`}
                    onClick={() =>
                      trackEvent(
                        "Web Profile",
                        navItem.name,
                        `Select ${navItem.name}`,
                      )
                    }
                  >
                    {navItem.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div className="col md:col-3/4 md:pt-section">{children}</div>
        </div>
      </div>
      <style jsx>{`
        .navigation {
          padding-top: 100px;
          padding-bottom: 100px;
        }
        .nav-item.selected,
        .nav-item:hover {
          font-weight: 600;
        }
        .nav-item.selected:before,
        .nav-item:hover:before {
          content: "";
          position: absolute;
          background-color: var(--color-green);
          height: 100%;
          width: 4px;
          left: -12px;
        }
        .isolate {
          isolation: isolate;
        }
      `}</style>
    </>
  );
};

export default UserProfileLayout;
